/**
 * Convenient logger utility for quick logging throughout the app
 * 
 * Usage examples:
 * 
 * import logger from './utils/logger';
 * 
 * logger.info('User clicked button', 'user_action', { buttonId: 'submit' });
 * logger.error('API call failed', 'api_call', { endpoint: '/users', status: 500 });
 * logger.debug('Component mounted', 'system', { componentName: 'Header' });
 */

import { addLog, LogLevel, LogCategory } from './circularBuffer';

class Logger {
  /**
   * Log an info message
   */
  info(message: string, category: LogCategory, context: Record<string, any> = {}): void {
    addLog('info', message, { category, ...context });
  }

  /**
   * Log a debug message
   */
  debug(message: string, category: LogCategory, context: Record<string, any> = {}): void {
    addLog('debug', message, { category, ...context });
  }

  /**
   * Log a warning message
   */
  warning(message: string, category: LogCategory, context: Record<string, any> = {}): void {
    addLog('warning', message, { category, ...context });
  }

  /**
   * Log an error message
   */
  error(message: string, category: LogCategory, context: Record<string, any> = {}): void {
    addLog('error', message, { category, ...context });
  }

  /**
   * Log with custom level
   */
  log(level: LogLevel, message: string, category: LogCategory, context: Record<string, any> = {}): void {
    addLog(level, message, { category, ...context });
  }

  /**
   * Log navigation events
   */
  navigation(pathname: string, context: Record<string, any> = {}): void {
    addLog('info', `Navigation: ${pathname}`, {
      category: 'navigation',
      pathname,
      ...context
    });
  }

  /**
   * Log API calls
   */
  apiCall(method: string, endpoint: string, status?: number, context: Record<string, any> = {}): void {
    const level: LogLevel = status && status >= 400 ? 'error' : 'info';
    addLog(level, `API ${method} ${endpoint}${status ? ` - ${status}` : ''}`, {
      category: 'api_call',
      method,
      endpoint,
      status,
      ...context
    });
  }

  /**
   * Log user actions
   */
  userAction(action: string, context: Record<string, any> = {}): void {
    addLog('info', `User action: ${action}`, {
      category: 'user_action',
      action,
      ...context
    });
  }

  /**
   * Log deep link events
   */
  deepLink(url: string, context: Record<string, any> = {}): void {
    addLog('info', `Deep link: ${url}`, {
      category: 'deep_link',
      url,
      ...context
    });
  }

  /**
   * Log performance metrics
   */
  performance(metric: string, value: number, context: Record<string, any> = {}): void {
    addLog('debug', `Performance: ${metric} = ${value}ms`, {
      category: 'performance',
      metric,
      value,
      ...context
    });
  }
}

// Export singleton instance
const logger = new Logger();
export default logger;
